import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import { debounce, throttle } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { findNextNavigableIndex, resolveNavBaseIndex } from './diffListNavUtils';

export type VirtuosoScrollerHandle = {
  scrollIntoView: (options: ScrollIntoViewOptions & { index: number; done?: () => void }) => void;
  scrollToIndex: (options: { index: number; align?: string; behavior?: ScrollBehavior }) => void;
};

export interface UseDifferenceListInteractionOptions {
  itemCount: number;
  getFocusedListIndex: () => number;
  /**
   * DifferenceElementView hotkeys pass `numberOfDifference + 1`.
   * Flat list: treat as list index. SnR: map difference number → list index.
   */
  resolveHotkeyTargetToListIndex: (hotkeyTarget: number) => number;
  isNavigableIndex?: (index: number) => boolean;
  onClickSelectExtra?: (listIndex: number) => void;
  initialCommentIds?: string[];
}

export interface UseDifferenceListInteractionResult {
  virtuoso: MutableRefObject<VirtuosoScrollerHandle | null>;
  currentItemIndex: number;
  clickSelect: boolean;
  setClickSelect: Dispatch<SetStateAction<boolean>>;
  openComments: Record<string, boolean>;
  onClickSelect: (listIndex: number) => void;
  getCardRef: (index: number) => (el: HTMLDivElement | null) => void;
  updateCommentOpen: (id: string, open: boolean, comment?: string) => void;
  /** Hotkey entry: maps via resolveHotkeyTargetToListIndex, then navigates by list index. */
  handleOnStopKeyboardNavigation: (hotkeyTarget: number, behavior?: ScrollBehavior) => void;
  navigateToListIndex: (listIndex: number, behavior?: ScrollBehavior) => void;
  scrollerRef: (element: HTMLElement | Window | null) => void;
  enableKeyboardNavigation: boolean;
  resetKeyboardFocus: () => void;
}

/** Shared keyboard / card-ref / comment state. Focus/re-sort scroll stays in each list. */
export const useDifferenceListInteraction = ({
  itemCount,
  getFocusedListIndex,
  resolveHotkeyTargetToListIndex,
  isNavigableIndex = () => true,
  onClickSelectExtra,
  initialCommentIds = [],
}: UseDifferenceListInteractionOptions): UseDifferenceListInteractionResult => {
  const flags = useFlags();
  const enableKeyboardNavigation = Boolean(flags?.enableKeyboardNavigation);

  const virtuoso = useRef<VirtuosoScrollerHandle | null>(null);
  const scrollerElementRef = useRef<HTMLElement | null>(null);
  const [clickSelect, setClickSelect] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(-1);
  const currentItemIndexRef = useRef(-1);
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const cardRefCallbacksRef = useRef(new Map<number, (el: HTMLDivElement | null) => void>());
  const navSeqRef = useRef(0);

  const [openComments, setOpenComments] = useState<Record<string, boolean>>(() => {
    const startingValues: Record<string, boolean> = {};
    initialCommentIds.forEach((id) => {
      startingValues[id] = false;
    });
    return startingValues;
  });

  useEffect(() => {
    currentItemIndexRef.current = currentItemIndex;
  }, [currentItemIndex]);

  const onClickSelect = useCallback(
    (listIndex: number) => {
      setCurrentItemIndex(listIndex);
      setClickSelect(true);
      onClickSelectExtra?.(listIndex);
    },
    [onClickSelectExtra],
  );

  const getCardRef = useCallback((index: number) => {
    if (!cardRefCallbacksRef.current.has(index)) {
      cardRefCallbacksRef.current.set(index, (el: HTMLDivElement | null) => {
        cardRefs.current[index] = el;
      });
    }
    return cardRefCallbacksRef.current.get(index)!;
  }, []);

  const navigateToListIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'auto') => {
      if (!enableKeyboardNavigation) return;
      if (index < 0 || index >= itemCount) return;

      const tryClick = (retries = 5) => {
        const element = cardRefs.current[index];
        if (element) {
          (element as HTMLElement).dataset.autoAdvance = 'true';
          element.click();
          window.setTimeout(() => {
            if ((element as HTMLElement).dataset.autoAdvance) {
              delete (element as HTMLElement).dataset.autoAdvance;
            }
          }, 0);
        } else if (retries > 0) {
          window.setTimeout(() => tryClick(retries - 1), 50);
        }
      };

      const shouldScroll = index !== currentItemIndexRef.current;
      if (shouldScroll && virtuoso.current && typeof virtuoso.current.scrollIntoView === 'function') {
        virtuoso.current.scrollIntoView({
          index,
          behavior,
          done: () => {
            tryClick();
          },
        });
      } else {
        tryClick();
      }
    },
    [itemCount, enableKeyboardNavigation],
  );

  const handleOnStopKeyboardNavigation = useCallback(
    (hotkeyTarget: number, behavior: ScrollBehavior = 'auto') => {
      const nextListIndex = resolveHotkeyTargetToListIndex(hotkeyTarget);
      if (nextListIndex === -1) return;
      navSeqRef.current += 1;
      navigateToListIndex(nextListIndex, behavior);
    },
    [resolveHotkeyTargetToListIndex, navigateToListIndex],
  );

  const updateCommentOpen = useCallback(
    (id: string, open: boolean, comment = '') => {
      setOpenComments((prevState) => ({ ...prevState, [id]: open }));

      // Ref (not state) keeps this callback stable so memoized cards skip re-renders.
      const currentIndex = currentItemIndexRef.current;
      if (comment && !open && currentIndex + 1 < itemCount) {
        setCurrentItemIndex((prevIndex) => (prevIndex + 1 < itemCount ? prevIndex + 1 : prevIndex));
        navigateToListIndex(currentIndex + 1);
      }
    },
    [itemCount, navigateToListIndex],
  );

  const debouncedOnStop = useCallback(
    debounce((index: number) => navigateToListIndex(index), 700),
    [navigateToListIndex],
  );

  const throttledKeyDownCallback = useCallback(
    throttle((e: KeyboardEvent) => {
      if (e.code !== 'ArrowUp' && e.code !== 'ArrowDown') return;

      const focusedListIndex = getFocusedListIndex();
      const baseIndex = resolveNavBaseIndex(currentItemIndex, focusedListIndex);
      const direction: 1 | -1 = e.code === 'ArrowUp' ? -1 : 1;
      const stepped = e.code === 'ArrowUp' ? Math.max(0, baseIndex - 1) : Math.min(itemCount - 1, baseIndex + 1);

      const nextIndex = findNextNavigableIndex(stepped, direction, itemCount, isNavigableIndex);
      if (nextIndex === -1 || !scrollerElementRef.current) return;
      if (!virtuoso.current || typeof virtuoso.current.scrollIntoView !== 'function') return;

      navSeqRef.current += 1;
      const mySeq = navSeqRef.current;

      virtuoso.current.scrollIntoView({
        index: nextIndex,
        behavior: 'auto',
        done: () => {
          if (navSeqRef.current !== mySeq) return;
          setCurrentItemIndex(nextIndex);
          debouncedOnStop(nextIndex);
        },
      });
      e.preventDefault();
    }, 300),
    [currentItemIndex, itemCount, getFocusedListIndex, isNavigableIndex, debouncedOnStop],
  );

  const scrollerRef = useCallback(
    (element: HTMLElement | Window | null) => {
      if (element && element instanceof HTMLElement) {
        element.addEventListener('keydown', throttledKeyDownCallback);
        element.id = 'diff-list-scroller';
        scrollerElementRef.current = element;
      } else if (scrollerElementRef.current) {
        scrollerElementRef.current.removeEventListener('keydown', throttledKeyDownCallback);
        scrollerElementRef.current = null;
      }
    },
    [throttledKeyDownCallback],
  );

  const resetKeyboardFocus = useCallback(() => {
    setCurrentItemIndex(-1);
  }, []);

  useEffect(() => {
    return () => {
      debouncedOnStop.cancel();
    };
  }, [debouncedOnStop]);

  return {
    virtuoso,
    currentItemIndex,
    clickSelect,
    setClickSelect,
    openComments,
    onClickSelect,
    getCardRef,
    updateCommentOpen,
    handleOnStopKeyboardNavigation,
    navigateToListIndex,
    scrollerRef,
    enableKeyboardNavigation,
    resetKeyboardFocus,
  };
};
